import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { SocketContext } from "../../../customComponents/SocketContext";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import { alertErrorMessage } from "../../../customComponents/CustomAlertMessage";
import Footer from "../../../customComponents/Footer";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import "./market-quote-select.css";
import MarketQuoteSelect from "./MarketQuoteSelect";
import MarketMiniSparkline from "./MarketMiniSparkline";
import Market24hRangeChart from "./Market24hRangeChart";
import Market24hPriceRange from "./Market24hPriceRange";
import {
  formatPrice,
  formatNumber,
  formatVolumeLabel,
  displaySpotChangePercent,
  isPositiveChange,
  spotChangeNumber,
  futuresContractsToMarketRows,
  mergeFuturesMarketContractUpdate,
} from "./marketFormatters";
import useOptionsWebSocket, { OPTIONS_CHANNELS } from "../options/useOptionsWebSocket";
import { getOptionsSocket } from "../options/optionsSocketManager";
import { resolveUnderlyingIconUrl } from "../options/optionsDataHelpers";

const ALPHA_CHAINS = [
  { id: "all", label: "All", icon: null },
  { id: "agcelayer", label: "AGCXLayer", icon: "/images/market-img/bitcoin_icon.svg" },
  { id: "sol", label: "Solana", icon: "/images/market-img/bitcoin_icon.svg" },
  { id: "bsc", label: "BSC", icon: "/images/market-img/bch_icon.svg" },
  { id: "eth", label: "Ethereum", icon: "/images/market-img/eth_icon.svg" },
  { id: "base", label: "Base", icon: "/images/market-img/eth_icon.svg" },
  { id: "arb", label: "Arbitrum One", icon: "/images/market-img/bitcoin_icon.svg" },
  { id: "sui", label: "Sui", icon: "/images/market-img/bitcoin_icon.svg" },
  { id: "avax", label: "AVAX C-Chain", icon: "/images/market-img/bitcoin_icon.svg" },
  { id: "world", label: "World", icon: "/images/market-img/bitcoin_icon.svg" },
];

function AlphaChainSelect() {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("all");
  const rootRef = useRef(null);

  const selected = useMemo(
    () => ALPHA_CHAINS.find((c) => c.id === selectedId) || ALPHA_CHAINS[0],
    [selectedId]
  );

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const iconsToShow = useMemo(() => {
    if (selectedId === "all") {
      return [
        "/images/market-img/bitcoin_icon.svg",
        "/images/market-img/bch_icon.svg",
        "/images/market-img/eth_icon.svg",
        "/images/market-img/eth_icon.svg",
      ];
    }
    return selected?.icon ? [selected.icon] : [];
  }, [selectedId, selected]);

  return (
    <div className={`market-alpha-chain${open ? " is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        className="market-alpha-filters__chainBtn"
        aria-label="All chains"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="market-alpha-filters__chainLabel">{selected.label}</span>
        <span className="market-alpha-filters__chainIcons" aria-hidden="true">
          {iconsToShow.map((src, idx) => (
            <img key={`${src}-${idx}`} className="market-alpha-filters__chainIcon" src={src} alt="" />
          ))}
          <span className="market-alpha-filters__chainIcon market-alpha-filters__chainIcon--dummy" aria-hidden="true">
            ≡
          </span>
        </span>
        <i className={`ri-arrow-${open ? "up" : "down"}-s-line market-alpha-filters__chev`} aria-hidden="true" />
      </button>

      {open ? (
        <div className="market-alpha-chain__panel" role="listbox" aria-label="Chains">
          {ALPHA_CHAINS.map((c) => (
            <button
              key={c.id}
              type="button"
              className="market-alpha-chain__opt"
              role="option"
              aria-selected={c.id === selectedId}
              onClick={() => {
                setSelectedId(c.id);
                setOpen(false);
              }}
            >
              <span className="market-alpha-chain__optLeft">
                {c.icon ? (
                  <img className="market-alpha-chain__optIcon" src={c.icon} alt="" />
                ) : (
                  <span className="market-alpha-chain__optIcon market-alpha-chain__optIcon--all" aria-hidden="true" />
                )}
                <span>{c.label}</span>
              </span>
              {c.id === selectedId ? (
                <span className="market-alpha-chain__check" aria-hidden="true">
                  <i className="ri-check-line" aria-hidden="true" />
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const ALPHA_POOLS = {
  dex: [
    { id: "all_dex", label: "ALL DEXs (191)", icon: null },
    { id: "agceswap", label: "AGCXSwap V2", sub: "24h Volume: 25.4K", icon: "/images/market-img/bitcoin_icon.svg" },
    { id: "meteora", label: "Meteora DAMM V2", sub: "24h Volume: 9.26B", icon: "/images/market-img/eth_icon.svg" },
    { id: "pumpswap", label: "PumpSwap", sub: "24h Volume: 5.15B", icon: "/images/market-img/bch_icon.svg" },
    { id: "pancake", label: "PancakeSwap V2", sub: "24h Volume: 9.96B", icon: "/images/market-img/bch_icon.svg" },
  ],
  launch: [
    { id: "all_launch", label: "All Launch Platforms", icon: null },
    { id: "launchpad", label: "Launchpad", sub: "24h Volume: 1.02B", icon: "/images/market-img/eth_icon.svg" },
    { id: "fairlaunch", label: "Fair Launch", sub: "24h Volume: 420.1M", icon: "/images/market-img/bitcoin_icon.svg" },
  ],
};

function AlphaPoolSelect() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("dex");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState("all_dex");
  const [draftId, setDraftId] = useState("all_dex");
  const rootRef = useRef(null);

  const items = useMemo(() => ALPHA_POOLS[tab] || [], [tab]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => (it.label + " " + (it.sub || "")).toLowerCase().includes(q));
  }, [items, query]);

  const selected = useMemo(() => {
    const all = [...ALPHA_POOLS.dex, ...ALPHA_POOLS.launch];
    return all.find((x) => x.id === selectedId) || { id: "all_dex", label: "Select Pool (All)" };
  }, [selectedId]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={`market-alpha-pool${open ? " is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        className="market-alpha-filters__poolBtn"
        aria-label="Select pool"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setDraftId(selectedId);
          setOpen((v) => !v);
        }}
      >
        <span>{selectedId === "all_dex" ? "Select Pool (All)" : selected.label}</span>
        <i className={`ri-arrow-${open ? "up" : "down"}-s-line market-alpha-filters__chev`} aria-hidden="true" />
      </button>

      {open ? (
        <div className="market-alpha-pool__panel" role="dialog" aria-label="Select Pool">
          <div className="market-alpha-pool__search">
            <i className="ri-search-line" aria-hidden="true" />
            <input
              type="search"
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="market-alpha-pool__tabs" role="tablist" aria-label="Pool type">
            <button
              type="button"
              className={`market-alpha-pool__tab${tab === "dex" ? " is-active" : ""}`}
              role="tab"
              aria-selected={tab === "dex"}
              onClick={() => setTab("dex")}
            >
              DEXs
            </button>
            <button
              type="button"
              className={`market-alpha-pool__tab${tab === "launch" ? " is-active" : ""}`}
              role="tab"
              aria-selected={tab === "launch"}
              onClick={() => setTab("launch")}
            >
              Launch Platforms
            </button>
          </div>

          <div className="market-alpha-pool__list" role="listbox" aria-label="Pools">
            {filtered.map((it) => (
              <button
                key={it.id}
                type="button"
                className={`market-alpha-pool__item${draftId === it.id ? " is-selected" : ""}`}
                role="option"
                aria-selected={draftId === it.id}
                onClick={() => setDraftId(it.id)}
              >
                <span className="market-alpha-pool__itemLeft">
                  {it.icon ? (
                    <img className="market-alpha-pool__itemIcon" src={it.icon} alt="" />
                  ) : (
                    <span className="market-alpha-pool__itemIcon market-alpha-pool__itemIcon--all" aria-hidden="true" />
                  )}
                  <span className="market-alpha-pool__itemText">
                    <span className="market-alpha-pool__itemTitle">{it.label}</span>
                    {it.sub ? <span className="market-alpha-pool__itemSub">{it.sub}</span> : null}
                  </span>
                </span>
                <span className="market-alpha-pool__tick" aria-hidden="true">
                  <i className="ri-check-line" aria-hidden="true" />
                </span>
              </button>
            ))}
          </div>

          <div className="market-alpha-pool__actions">
            <button
              type="button"
              className="market-alpha-pool__btn market-alpha-pool__btn--ghost"
              onClick={() => {
                setDraftId(tab === "dex" ? "all_dex" : "all_launch");
                setQuery("");
              }}
            >
              Reset
            </button>
            <button
              type="button"
              className="market-alpha-pool__btn market-alpha-pool__btn--primary"
              onClick={() => {
                setSelectedId(draftId);
                setOpen(false);
              }}
            >
              Apply
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function buildSpotChartSeries(item, hotPairsChart) {
  if (!item) return [];
  const base = item?.base_currency;
  if (base && hotPairsChart && hotPairsChart[base]) {
    return hotPairsChart[base];
  }
  if (item?.chart_data && Array.isArray(item.chart_data)) {
    return item.chart_data;
  }
  return [];
}

function formatSubCategoryLabel(key) {
  if (!key || key === "All") return "All";
  return String(key)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const SPOT_QUOTE_PRESET = ["USDT", "BTC", "USDC", "ETH", "BNB"];

function normSym(v) {
  return String(v ?? "")
    .trim()
    .toUpperCase();
}

function buildSpotQuoteOptions(pairs) {
  const s = new Set(SPOT_QUOTE_PRESET);
  if (Array.isArray(pairs)) {
    pairs.forEach((p) => {
      if (p?.quote_currency) s.add(normSym(p.quote_currency));
      if (p?.base_currency) s.add(normSym(p.base_currency));
    });
  }
  return ["All", ...Array.from(s).filter(Boolean).sort((a, b) => a.localeCompare(b))];
}

/** Settle / margin for futures: socket payloads may use margin_asset, quote_asset, quote, or quote_currency. */
function getFuturesMarginSymbol(item) {
  if (!item) return "USDT";
  const m =
    item.margin_asset ??
    item.marginAsset ??
    item.quote_asset ??
    item.quote_currency ??
    item.quote;
  if (m == null || String(m).trim() === "") return "USDT";
  return String(m).trim();
}

function buildFuturesQuoteOptions(futures) {
  const s = new Set();
  if (Array.isArray(futures)) {
    futures.forEach((p) => {
      s.add(normSym(getFuturesMarginSymbol(p)));
    });
  }
  if (s.size === 0) s.add("USDT");
  return ["All", ...Array.from(s).sort((a, b) => a.localeCompare(b))];
}

function PairTableRow({ item, index, token, favCoins, onFav, onTrade, onDetails, onDeposit, variant = "default", showDollarPrice = true }) {
  const isCryptos = variant === "cryptos";
  const isPos = isPositiveChange(item);
  const chClass = isPos ? "color-green green" : "color-red text-danger";
  const last = formatPrice(item?.buy_price, item?.tick_size);
  const sym = (item?.base_currency || "—")?.toString();
  const quote = (item?.quote_currency || "—")?.toString();
  const name =
    item?.base_currency_fullname ||
    item?.name ||
    item?.base_currency_f_name ||
    item?.coin_name ||
    item?.base_currency ||
    "—";
  const imgSrc = item?.icon_path ? `${resolvePublicUploadUrl(item.icon_path)}` : "/images/AnnouncementImg/activities_icon.png";

  const nameCell = (
    <td>
      <div className="td_div">
        {token && (
          <span className="star_btn btn_icon active" role="button" tabIndex={0} onClick={(e) => onFav(e, item?._id)}>
            <i
              className={
                item?._id && favCoins?.includes?.(item._id) ? "ri-star-fill text-warning me-2" : "ri-star-line me-2"
              }
            ></i>
          </span>
        )}
        <img
          alt={sym}
          src={imgSrc}
          className="img-fluid icon_img coinimg me-2"
          onError={(e) => {
            e.target.src = "/images/AnnouncementImg/activities_icon.png";
          }}
        />
        <div className="coin_info">
          <div className="coin_name_lft">
            {isCryptos ? (
              sym
            ) : (
              <>
                {sym}
                <span className="coin_symbol">/{quote}</span>
              </>
            )}
          </div>
          <span className="coin_name">{name}</span>
        </div>
      </div>
    </td>
  );

  if (isCryptos) {
    return (
      <tr key={item?._id || index} onClick={() => onTrade(item)}>
        {nameCell}
        <td>
          <div className="last_price">
            {last}
            <span className="price_value">${last}</span>
          </div>
        </td>
        <td className={chClass}>
          <div className="hight_price">{displaySpotChangePercent(item)}</div>
        </td>
        <td>
          <div className="chart_range">
            <Market24hPriceRange
              low={item?.low}
              high={item?.high}
              lastPrice={item?.buy_price}
              tickSize={item?.tick_size}
            />
          </div>
        </td>
        <td className="right_0">
          <div className="btb_tbl d-flex">
            <button type="button" className="btn details_btn" onClick={(e) => onDeposit(e, item)}>
              Deposit
            </button>
            <button type="button" className="btn trade_btn" onClick={(e) => onDetails(e, item)}>
              Trade
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr key={item?._id || index} onClick={() => onTrade(item)}>
      {nameCell}
      <td>
        <div className="last_price">
          {last}
          {showDollarPrice ? <span className="price_value">${last}</span> : null}
        </div>
      </td>
      <td className={chClass}>
        <div className="hight_price">{displaySpotChangePercent(item)}</div>
      </td>
      <td>
        <div className="chart_range">
          <Market24hRangeChart
            isPositive={isPos}
            low={item?.low}
            high={item?.high}
            lastPrice={item?.buy_price}
            openPrice={item?.open_24h ?? item?.open_price ?? item?.open}
          />
        </div>
      </td>
      <td>
        <div className="chart_range">
          <Market24hPriceRange
            low={item?.low}
            high={item?.high}
            lastPrice={item?.buy_price}
            tickSize={item?.tick_size}
          />
        </div>
      </td>
      <td>
        <div className="volume_price">{formatVolumeLabel(item?.volume)}</div>
      </td>
      <td className="right_0">
        <div className="btb_tbl d-flex">
          <button
            type="button"
            className="btn details_btn"
            onClick={(e) => onDeposit(e, item)}
          >
            Deposit
          </button>
          <button
            type="button"
            className="btn trade_btn"
            onClick={(e) => onDetails(e, item)}
          >
            Trade
          </button>
        </div>
      </td>
    </tr>
  );
}

const FEATURED_CARD_SKELETON_SLOTS = [0, 1, 2];

/** Full-width empty state without a table header (same visual language as COIN-M / Options placeholders). */
function MarketTableEmptyPanel({ message, children }) {
  return (
    <div className="market-futures-placeholder market-table-empty-panel" role="status">
      <div className="market-fav-empty">
        <img
          src="/images/no-data.svg"
          alt=""
          width={120}
          height={144}
          className="market-fav-empty__img"
        />
        {children ?? (message ? <p className="market-fav-empty__caption">{message}</p> : null)}
      </div>
    </div>
  );
}

function MarketFeaturedCardSkeleton({ layout = "desktop", index = 0 }) {
  const stagger = { "--mfs-stagger": `${index * 100}ms` };
  const rootClass =
    layout === "mobile"
      ? "trade_marketvalue market-featured-card-skeleton market-featured-card-skeleton--mobile"
      : "trade_marketvalue market-featured-card-skeleton";
  return (
    <div className={rootClass} style={stagger} aria-hidden>
      <div className="market_value_card w-100">
        <div className="d-flex tophd align-items-center">
          <h5 className="market-featured-card-skeleton__pair mb-0 flex-grow-1">
            <span className="market-featured-card-skeleton__bone market-featured-card-skeleton__icon" />
            <span className="market-featured-card-skeleton__bone market-featured-card-skeleton__sym" />
          </h5>
          <span className="market-featured-card-skeleton__bone market-featured-card-skeleton__chg" />
        </div>
        <div className="price">
          <span className="market-featured-card-skeleton__bone market-featured-card-skeleton__price" />
        </div>
      </div>
      <div className="privevolume">
        <span className="market-featured-card-skeleton__bone market-featured-card-skeleton__vol" />
      </div>
      <div className="tradevector_r market-featured-card-skeleton__chart-wrap">
        <span className="market-featured-card-skeleton__bone market-featured-card-skeleton__chart" />
      </div>
    </div>
  );
}

function FuturesTableRow({ item, index, onFutures, onFuturesClick }) {
  const tick = item?.tickSize || item?.tick_size;
  const priceDecimals =
    item?.price_precision != null && item?.price_precision !== ""
      ? Number(item.price_precision)
      : null;
  const isPos = Number(item?.change_percentage) >= 0;
  const chClass = isPos ? "color-green green" : "color-red text-danger";
  const last =
    priceDecimals != null && Number.isFinite(priceDecimals)
      ? formatNumber(item?.buy_price, priceDecimals)
      : formatPrice(item?.buy_price, tick);
  const sym = (item?.short_name || "—")?.toString();
  const margin = getFuturesMarginSymbol(item);
  const name =
    item?.base_currency_fullname || item?.name || item?.short_name || "—";
  const imgSrc = item?.icon_path ? `${resolvePublicUploadUrl(item.icon_path)}` : "/images/AnnouncementImg/activities_icon.png";
  return (
    <tr key={item?._id || index} onClick={() => onFutures(item)}>
      <td>
        <div className="td_div">
          {/* <span className="star_btn btn_icon active" aria-hidden>
            <i className="ri-star-fill text-warning me-2"></i>
          </span> */}
          <img
            alt={sym}
            src={imgSrc}
            className="img-fluid icon_img coinimg me-2"
            onError={(e) => {
              e.target.src = "/images/AnnouncementImg/activities_icon.png";
            }}
          />
          <div className="coin_info">
            <div className="coin_name_lft">
              {sym}
              <span className="coin_symbol">/{margin}</span>
            </div>
            <span className="coin_name">{name}</span>
          </div>
        </div>
      </td>
      <td>
        <div className="last_price">
          {last}
          <span className="price_value">${last}</span>
        </div>
      </td>
      <td className={chClass}>
        <div className="hight_price">
          {isPos ? "+" : ""}
          {formatNumber(item?.change_percentage)}%
        </div>
      </td>
      <td>
        <div className="chart_range">
          <Market24hRangeChart
            isPositive={isPos}
            low={item?.low}
            high={item?.high}
            lastPrice={item?.buy_price}
            openPrice={item?.open_24h ?? item?.open_price ?? item?.open}
          />
        </div>
      </td>
      <td>
        <div className="chart_range">
          <Market24hPriceRange
            low={item?.low}
            high={item?.high}
            lastPrice={item?.buy_price}
            tickSize={tick}
          />
        </div>
      </td>
      <td>
        <div className="volume_price">{formatVolumeLabel(item?.volume)}</div>
      </td>
      {/* <td>
        <div className="market_cap">—</div>
      </td> */}
      <td className="right_0">
        <div className="btb_tbl d-flex">
          <button type="button" className="btn details_btn" onClick={(e) => onFuturesClick(e, item)}>
            Details
          </button>
          <button type="button" className="btn trade_btn" onClick={(e) => onFuturesClick(e, item)}>
            Trade
          </button>
        </div>
      </td>
    </tr>
  );
}

function optionAssetFromSymbol(symbol) {
  const m = String(symbol || "").match(/^([A-Z0-9]+)-/i);
  return m ? m[1].toUpperCase() : "";
}

function parseOptionNum(v) {
  if (v == null || v === "") return null;
  if (typeof v === "object" && v.$numberDecimal != null) {
    const n = parseFloat(String(v.$numberDecimal));
    return Number.isFinite(n) ? n : null;
  }
  const n = parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

function formatOpenInterest(v) {
  const n = parseOptionNum(v);
  if (n == null) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function toOptionsUnderlyingKey(value) {
  if (value == null) return "";
  if (typeof value === "object") {
    return toOptionsUnderlyingKey(value.underlying || value.symbol || value.asset || "");
  }
  const s = String(value).trim().toUpperCase();
  if (!s) return "";
  if (s.includes("-")) {
    const asset = s.split("-")[0];
    if (!asset) return "";
    if (asset.endsWith("USDT") || asset.endsWith("USDC")) return asset;
    return `${asset}USDT`;
  }
  if (s.endsWith("USDT") || s.endsWith("USDC")) return s;
  return `${s}USDT`;
}

function extractOptionsContracts(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.contracts)) return data.contracts;
  if (Array.isArray(data?.data?.contracts)) return data.data.contracts;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function formatOptionsPx(v, tick) {
  const n = parseOptionNum(v);
  if (n == null) return "—";
  return formatPrice(n, tick);
}

function formatOptionsStrike(v) {
  const n = parseOptionNum(v);
  if (n == null) return "";
  return Number.isInteger(n) ? String(n) : String(n);
}

const OPTIONS_FAV_STORAGE_KEY = "agcx_options_favorites";

function readOptionsFavSymbols() {
  try {
    const parsed = JSON.parse(localStorage.getItem(OPTIONS_FAV_STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.map((s) => String(s || "")).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function writeOptionsFavSymbols(symbols) {
  localStorage.setItem(OPTIONS_FAV_STORAGE_KEY, JSON.stringify(symbols));
}

function mapOptionsContractToMarketRow(contract, overview) {
  if (!contract || typeof contract !== "object") return null;
  const symbol = String(contract.symbol || "");
  if (!symbol) return null;
  const asset = optionAssetFromSymbol(symbol);
  const underlyingKey = toOptionsUnderlyingKey(symbol);
  const icons = overview?.underlying_icons || {};
  const optionType = String(contract.option_type || "").toUpperCase();
  return {
    ...contract,
    symbol,
    asset,
    underlyingKey,
    optionType,
    strike: parseOptionNum(contract.strike),
    iconPath: icons[underlyingKey] || icons[asset] || null,
    buy_price: parseOptionNum(contract.mark_price),
    bid: parseOptionNum(contract.bid_price),
    ask: parseOptionNum(contract.ask_price),
    volume: parseOptionNum(contract.volume),
    open_interest: parseOptionNum(contract.open_interest),
    tick_size: contract.tick_size,
  };
}

function OptionsTableRow({ item, index, favSymbols, onFav, onTrade, onTradeClick }) {
  const navigate = useNavigate();
  const [localFavs, setLocalFavs] = useState(readOptionsFavSymbols);
  const symbol = item?.symbol || "—";
  const tick = item?.tick_size;
  const lastNum = parseOptionNum(item?.buy_price);
  const last = formatOptionsPx(item?.buy_price, tick);
  const bid = formatOptionsPx(item?.bid, tick);
  const ask = formatOptionsPx(item?.ask, tick);
  const volume = parseOptionNum(item?.volume);
  const typeLabel = item?.optionType === "PUT" ? "Put" : item?.optionType === "CALL" ? "Call" : (item?.optionType || "");
  const strikeLabel = formatOptionsStrike(item?.strike);
  const sub = [typeLabel, strikeLabel].filter(Boolean).join(" · ");
  const favList = Array.isArray(favSymbols) ? favSymbols : localFavs;
  const isFav = Boolean(symbol && symbol !== "—" && favList.includes(symbol));

  const handleStarClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const key = String(item?.symbol || "").trim();
    if (!key) return;
    if (!localStorage.getItem("token")) {
      navigate("/login");
      return;
    }
    if (typeof onFav === "function") {
      onFav(e, key);
      return;
    }
    const prev = readOptionsFavSymbols();
    const next = prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key];
    writeOptionsFavSymbols(next);
    setLocalFavs(next);
  };

  return (
    <tr key={item?.symbol || index} onClick={() => onTrade?.(item)}>
      <td>
        <div className="td_div">
          <span
            className="star_btn btn_icon active"
            role="button"
            tabIndex={0}
            aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
            onClick={handleStarClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleStarClick(e);
            }}
          >
            <i className={isFav ? "ri-star-fill text-warning me-2" : "ri-star-line me-2"}></i>
          </span>
          <img
            alt={item?.asset || symbol}
            src={resolveUnderlyingIconUrl(item?.iconPath)}
            className="img-fluid icon_img coinimg me-2"
            onError={(e) => {
              e.target.src = "/images/AnnouncementImg/activities_icon.png";
            }}
          />
          <div className="coin_info">
            <div className="coin_name_lft">{symbol}</div>
            <span className="coin_name">{sub || item?.asset || ""}</span>
          </div>
        </div>
      </td>
      <td>
        <div className="last_price">
          {last}
          {lastNum != null ? <span className="price_value">${last}</span> : null}
        </div>
      </td>
      <td>
        <div className="hight_price">{bid}</div>
      </td>
      <td>
        <div className="hight_price">{ask}</div>
      </td>
      <td>
        <div className="volume_price">{volume != null ? formatVolumeLabel(volume) : "—"}</div>
      </td>
      <td>
        <div className="volume_price">{formatOpenInterest(item?.open_interest)}</div>
      </td>
      <td className="right_0">
        <button
          type="button"
          className="market-options-action-btn"
          aria-label="Open chart"
          onClick={(e) => onTradeClick(e, item)}
          style={{
            background: "transparent",
            border: 0,
            padding: 0,
            color: "inherit",
            fontSize: 18,
            lineHeight: 1,
          }}
        >
          <i className="ri-stock-line" aria-hidden="true" />
        </button>
      </td>
    </tr>
  );
}

/** Horizontal tab strip with prev/next arrows only when content overflows (market rows + Alpha row). */
function MarketTblTabsScroll({ children, recalcKey, className }) {
  const viewportRef = useRef(null);
  const [ui, setUi] = useState({ needsScroll: false, canLeft: false, canRight: false });

  const recompute = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    const threshold = 8;
    const needsScroll = maxScrollLeft > threshold;
    const canLeft = el.scrollLeft > 1;
    const canRight = el.scrollLeft < maxScrollLeft - 1;
    setUi({ needsScroll, canLeft, canRight });
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const schedule = () => window.requestAnimationFrame(recompute);
    const ro = new ResizeObserver(schedule);
    ro.observe(el);

    const io =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) schedule();
          },
          { threshold: 0.01 }
        )
        : null;
    io?.observe(el);

    const onScroll = () => recompute();
    window.addEventListener("resize", schedule);
    el.addEventListener("scroll", onScroll, { passive: true });
    schedule();

    return () => {
      ro.disconnect();
      io?.disconnect();
      window.removeEventListener("resize", schedule);
      el.removeEventListener("scroll", onScroll);
    };
  }, [recompute]);

  useEffect(() => {
    recompute();
  }, [recalcKey, recompute]);

  const scrollNav = useCallback((dir) => {
    const el = viewportRef.current;
    if (!el) return;
    const step = Math.min(280, Math.max(120, Math.floor(el.clientWidth * 0.72)));
    el.scrollBy({ left: dir === "next" ? step : -step, behavior: "smooth" });
  }, []);

  const showLeft = ui.needsScroll && ui.canLeft;
  const showRight = ui.needsScroll && ui.canRight;

  return (
    <div className={["market-tbltabs-scroll", className].filter(Boolean).join(" ")}>
      <button
        type="button"
        className={`market-tbltabs-scroll__btn market-tbltabs-scroll__btn--prev${showLeft ? "" : " is-hidden"}`}
        aria-label="Scroll tabs left"
        tabIndex={showLeft ? 0 : -1}
        onClick={() => scrollNav("prev")}
      >
        <i className="ri-arrow-left-s-line" aria-hidden="true" />
      </button>
      <div className="market-tbltabs-scroll__viewport" ref={viewportRef}>
        {children}
      </div>
      <button
        type="button"
        className={`market-tbltabs-scroll__btn market-tbltabs-scroll__btn--next${showRight ? "" : " is-hidden"}`}
        aria-label="Scroll tabs right"
        tabIndex={showRight ? 0 : -1}
        onClick={() => scrollNav("next")}
      >
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
      </button>
    </div>
  );
}

const Market = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const { socket, isConnected, marketData, subscribeToMarket, unsubscribeFromMarket, subscribeToFutures, unsubscribeFromFutures } =
    useContext(SocketContext);
  const [search, setSearch] = useState("");
  const [favCoins, setFavCoins] = useState([]);
  const [favOptions, setFavOptions] = useState(() => readOptionsFavSymbols());
  const [coinData, setCoinData] = useState([]);
  const [futuresPairData, setFuturesPairData] = useState([]);
  const [hotPairsChart, setHotPairsChart] = useState({});
  const [activeMarketTab, setActiveMarketTab] = useState("cryptos");
  const futuresTabActive = activeMarketTab === "futures-usdm";
  const optionsTabActive = activeMarketTab === "options";
  /** Keep options socket warm after first Options visit; only unsubscribe on tab change. */
  const [optionsSocketArmed, setOptionsSocketArmed] = useState(false);
  useEffect(() => {
    if (optionsTabActive) setOptionsSocketArmed(true);
  }, [optionsTabActive]);
  const [optionsAsset, setOptionsAsset] = useState("All");
  const {
    marketOverview,
    isMarketLoading: isOptionsMarketLoading,
    isConnected: optionsWsConnected,
  } = useOptionsWebSocket({
    token,
    selectedAsset: "",
    selectedExpiry: "ALL",
    // Arm once on first Options open; stay connected while Market page is mounted.
    enabled: optionsSocketArmed,
    // Channels only while Options tab is visible — leave tab = unsubscribe, not disconnect.
    subscribeMarketOverview: optionsTabActive,
    subscribeContracts: false, // Market page multi-subscribes underlyings below
    subscribeAccount: false,
    subscribeUserOrders: false,
    subscribeUserPositions: false,
    subscribeContractDetail: false,
  });
  const [optionsContractsByUnderlying, setOptionsContractsByUnderlying] = useState({});
  const [pairQuote, setPairQuote] = useState("All");
  const [futuresQuote, setFuturesQuote] = useState("All");
  /** default | desc (highest first) | asc (lowest first) — only one column active at a time */
  const [changeSort, setChangeSort] = useState("default");
  const [priceSort, setPriceSort] = useState("default");
  const [spotSubCategory, setSpotSubCategory] = useState("All");
  const gainerElementRef = useRef(null);

  const spotQuoteOptions = useMemo(() => buildSpotQuoteOptions(coinData), [coinData]);
  const futuresQuoteOptions = useMemo(() => buildFuturesQuoteOptions(futuresPairData), [futuresPairData]);

  const spotDataUnavailable = isConnected && coinData.length === 0;
  const futuresDataUnavailable = futuresTabActive && isConnected && futuresPairData.length === 0;

  useEffect(() => {
    if (pairQuote === "All") return;
    if (!spotQuoteOptions.includes(pairQuote)) setPairQuote("All");
  }, [pairQuote, spotQuoteOptions]);

  useEffect(() => {
    if (futuresQuote === "All") return;
    if (!futuresQuoteOptions.includes(futuresQuote)) setFuturesQuote("All");
  }, [futuresQuote, futuresQuoteOptions]);

  useEffect(() => {
    const tabs = document.getElementById("marketMainTabs");
    if (!tabs) return undefined;
    const onShown = (event) => {
      const id = event.target?.id;
      if (id === "market-futures-usdm-tab") setActiveMarketTab("futures-usdm");
      else if (id === "market-spot-tab") setActiveMarketTab("spot");
      else if (id === "market-fav-tab") setActiveMarketTab("fav");
      else if (id === "market-cryptos-tab") setActiveMarketTab("cryptos");
      else if (id === "market-alpha-tab") setActiveMarketTab("alpha");
      else if (id === "market-futures-options-tab") setActiveMarketTab("options");
      else setActiveMarketTab("other");
    };
    tabs.addEventListener("shown.bs.tab", onShown);
    return () => tabs.removeEventListener("shown.bs.tab", onShown);
  }, []);

  useEffect(() => {
    if (socket) {
      subscribeToMarket();
      return () => {
        unsubscribeFromMarket();
      };
    }
  }, [socket, subscribeToMarket, unsubscribeFromMarket]);

  useEffect(() => {
    if (!socket || !isConnected || !futuresTabActive) return undefined;

    const onFuturesUpdate = (data) => {
      if (!data || typeof data !== "object") return;

      if (data.type === "contracts_only" && Array.isArray(data.contracts)) {
        setFuturesPairData(futuresContractsToMarketRows(data.contracts));
        return;
      }

      if (data.type === "contract_data" && data.contract) {
        setFuturesPairData((prev) => mergeFuturesMarketContractUpdate(prev, data.contract));
      }
    };

    socket.on("futures:update", onFuturesUpdate);
    subscribeToFutures({});

    return () => {
      socket.off("futures:update", onFuturesUpdate);
      unsubscribeFromFutures();
      setFuturesPairData([]);
    };
  }, [socket, isConnected, futuresTabActive, subscribeToFutures, unsubscribeFromFutures]);

  useEffect(() => {
    if (marketData == null) return;
    setCoinData(Array.isArray(marketData.pairs) ? marketData.pairs : []);
    setHotPairsChart(
      marketData.hot_pairs_chart && typeof marketData.hot_pairs_chart === "object"
        ? marketData.hot_pairs_chart
        : {}
    );
  }, [marketData]);

  useEffect(() => {
    if (token) {
      (async () => {
        try {
          const result = await AuthService.favoriteList();
          if (result?.success) {
            setFavCoins(result?.data?.pairs || []);
          }
        } catch {
          // ignore
        }
      })();
    }
  }, [token]);

  const optionsUnderlyingKey = useMemo(() => {
    const list = marketOverview?.underlyings;
    if (!Array.isArray(list)) return "";
    return list.map((u) => String(u || "").trim().toUpperCase()).filter(Boolean).join("|");
  }, [marketOverview?.underlyings]);
  const optionsUnderlyingKeys = useMemo(
    () => (optionsUnderlyingKey ? optionsUnderlyingKey.split("|") : []),
    [optionsUnderlyingKey]
  );

  useEffect(() => {
    if (!optionsTabActive || !optionsWsConnected) return undefined;
    const sock = getOptionsSocket();
    if (!sock) return undefined;
    const keys = optionsUnderlyingKey ? optionsUnderlyingKey.split("|") : [];
    if (!keys.length) return undefined;

    const onContractsUpdate = (data) => {
      if (!data || typeof data !== "object") return;
      const list = extractOptionsContracts(data);
      const key = String(data.underlying || "").toUpperCase();
      if (!key) return;
      setOptionsContractsByUnderlying((prev) => ({ ...prev, [key]: list }));
    };

    sock.on("contracts_update", onContractsUpdate);
    keys.forEach((underlying) => {
      sock.emit("subscribe", {
        channel: OPTIONS_CHANNELS.CONTRACTS,
        underlying,
        expiry: "ALL",
      });
    });

    return () => {
      sock.off("contracts_update", onContractsUpdate);
      keys.forEach((underlying) => {
        sock.emit("unsubscribe", {
          channel: OPTIONS_CHANNELS.CONTRACTS,
          underlying,
          expiry: "ALL",
        });
      });
      // Fallback if server unsubscribes by channel only
      sock.emit("unsubscribe", { channel: OPTIONS_CHANNELS.CONTRACTS });
    };
  }, [optionsTabActive, optionsWsConnected, optionsUnderlyingKey]);

  useEffect(() => {
    if (optionsTabActive) return undefined;
    setOptionsContractsByUnderlying({});
    return undefined;
  }, [optionsTabActive]);

  const filterPairData = useMemo(() => {
    if (!coinData || coinData.length === 0) return [];
    let rows = [...coinData];
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter(
        (item) =>
          item?.base_currency?.toLowerCase?.()?.includes(s) ||
          item?.quote_currency?.toLowerCase?.()?.includes(s)
      );
    }
    if (pairQuote !== "All") {
      const q = normSym(pairQuote);
      rows = rows.filter((item) => normSym(item?.base_currency) === q || normSym(item?.quote_currency) === q);
    }
    if (spotSubCategory !== "All") {
      rows = rows.filter(
        (item) => String(item?.sub_category || "").trim() === spotSubCategory
      );
    }
    const n = (x) => {
      const v = typeof x === "string" ? Number(x) : Number(x);
      return Number.isFinite(v) ? v : 0;
    };
    if (changeSort === "desc") {
      rows = [...rows].sort((a, b) => spotChangeNumber(b) - spotChangeNumber(a));
    } else if (changeSort === "asc") {
      rows = [...rows].sort((a, b) => spotChangeNumber(a) - spotChangeNumber(b));
    } else if (priceSort === "desc") {
      rows = [...rows].sort((a, b) => n(b?.buy_price) - n(a?.buy_price));
    } else if (priceSort === "asc") {
      rows = [...rows].sort((a, b) => n(a?.buy_price) - n(b?.buy_price));
    }
    return rows;
  }, [coinData, search, pairQuote, spotSubCategory, changeSort, priceSort]);

  /** Cryptos tab: one row per base; prefer a USDT-quoted market as the row when both exist. */
  const filterCryptosData = useMemo(() => {
    const byBase = new Map();
    const usdt = "USDT";
    for (const item of filterPairData) {
      const b = normSym(item?.base_currency);
      if (!b) continue;
      if (!byBase.has(b)) {
        byBase.set(b, item);
        continue;
      }
      const cur = byBase.get(b);
      if (normSym(item?.quote_currency) === usdt && normSym(cur?.quote_currency) !== usdt) {
        byBase.set(b, item);
      }
    }
    let rows = Array.from(byBase.values());
    const n = (x) => {
      const v = typeof x === "string" ? Number(x) : Number(x);
      return Number.isFinite(v) ? v : 0;
    };
    if (changeSort === "desc") {
      rows = [...rows].sort((a, b) => spotChangeNumber(b) - spotChangeNumber(a));
    } else if (changeSort === "asc") {
      rows = [...rows].sort((a, b) => spotChangeNumber(a) - spotChangeNumber(b));
    } else if (priceSort === "desc") {
      rows = [...rows].sort((a, b) => n(b?.buy_price) - n(a?.buy_price));
    } else if (priceSort === "asc") {
      rows = [...rows].sort((a, b) => n(a?.buy_price) - n(b?.buy_price));
    }
    return rows;
  }, [filterPairData, changeSort, priceSort]);

  const spotSubCategories = useMemo(() => {
    if (!coinData?.length) return [];
    const s = new Set();
    coinData.forEach((p) => {
      const sc = p?.sub_category;
      if (sc != null && String(sc).trim() !== "") {
        s.add(String(sc).trim());
      }
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [coinData]);

  useEffect(() => {
    if (spotSubCategory !== "All" && !spotSubCategories.includes(spotSubCategory)) {
      setSpotSubCategory("All");
    }
  }, [spotSubCategory, spotSubCategories]);

  const filterOptionsData = useMemo(() => {
    const contracts = Object.values(optionsContractsByUnderlying).flatMap((list) =>
      Array.isArray(list) ? list : []
    );
    let rows = contracts
      .map((c) => mapOptionsContractToMarketRow(c, marketOverview))
      .filter(Boolean);
    if (optionsAsset && optionsAsset !== "All") {
      const q = normSym(optionsAsset);
      rows = rows.filter((item) => normSym(item?.underlyingKey) === q || normSym(item?.asset) === q);
    }
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter((item) => {
        return (
          item?.symbol?.toLowerCase?.()?.includes(s) ||
          item?.asset?.toLowerCase?.()?.includes(s) ||
          item?.optionType?.toLowerCase?.()?.includes(s) ||
          String(item?.strike ?? "").toLowerCase().includes(s)
        );
      });
    }
    const n = (x) => {
      const v = typeof x === "string" ? Number(x) : Number(x);
      return Number.isFinite(v) ? v : 0;
    };
    if (changeSort === "desc") {
      rows = [...rows].sort((a, b) => n(b?.change_percentage) - n(a?.change_percentage));
    } else if (changeSort === "asc") {
      rows = [...rows].sort((a, b) => n(a?.change_percentage) - n(b?.change_percentage));
    } else if (priceSort === "desc") {
      rows = [...rows].sort((a, b) => n(b?.buy_price) - n(a?.buy_price));
    } else if (priceSort === "asc") {
      rows = [...rows].sort((a, b) => n(a?.buy_price) - n(b?.buy_price));
    }
    return rows;
  }, [optionsContractsByUnderlying, marketOverview, optionsAsset, search, changeSort, priceSort]);

  const optionsQuoteOptions = useMemo(() => {
    const list = Array.isArray(marketOverview?.underlyings)
      ? marketOverview.underlyings
        .map((u) => String(u || "").trim().replace(/USDT$|USDC$/i, ""))
        .filter(Boolean)
      : [];
    return list.length ? ["All", ...list] : ["All"];
  }, [marketOverview]);

  useEffect(() => {
    if (!optionsQuoteOptions.includes(optionsAsset)) setOptionsAsset("All");
  }, [optionsQuoteOptions, optionsAsset]);

  const hasOptionsContracts = Object.keys(optionsContractsByUnderlying).length > 0;
  const optionsLoading =
    optionsTabActive &&
    !hasOptionsContracts &&
    (!optionsWsConnected || isOptionsMarketLoading || optionsUnderlyingKeys.length > 0);
  const optionsDataUnavailable =
    optionsTabActive &&
    optionsWsConnected &&
    !optionsLoading &&
    filterOptionsData.length === 0 &&
    !search;

  const filterFuturesData = useMemo(() => {
    if (!futuresPairData || futuresPairData.length === 0) return [];
    let rows = futuresPairData.filter(
      (item) => !item?.contract_category || normSym(item.contract_category) === "USD_M"
    );
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter(
        (item) =>
          item?.short_name?.toLowerCase?.()?.includes(s) ||
          item?.base_asset?.toLowerCase?.()?.includes(s) ||
          item?.base_currency?.toLowerCase?.()?.includes(s) ||
          item?.symbol?.toLowerCase?.()?.includes(s) ||
          item?.name?.toLowerCase?.()?.includes(s) ||
          item?.base_currency_fullname?.toLowerCase?.()?.includes(s)
      );
    }
    if (futuresQuote !== "All") {
      const q = normSym(futuresQuote);
      rows = rows.filter((item) => normSym(getFuturesMarginSymbol(item)) === q);
    }
    const n = (x) => {
      const v = typeof x === "string" ? Number(x) : Number(x);
      return Number.isFinite(v) ? v : 0;
    };
    const fChg = (it) => n(it?.change_percentage);
    if (changeSort === "desc") {
      rows = [...rows].sort((a, b) => fChg(b) - fChg(a));
    } else if (changeSort === "asc") {
      rows = [...rows].sort((a, b) => fChg(a) - fChg(b));
    } else if (priceSort === "desc") {
      rows = [...rows].sort((a, b) => n(b?.buy_price) - n(a?.buy_price));
    } else if (priceSort === "asc") {
      rows = [...rows].sort((a, b) => n(a?.buy_price) - n(b?.buy_price));
    }
    return rows;
  }, [futuresPairData, search, futuresQuote, changeSort, priceSort]);

  const featuredCoins = useMemo(() => {
    if (!coinData || coinData.length === 0) return [];
    const preferred = ["BTC", "ETH", "BNB"];
    const out = [];
    for (const sym of preferred) {
      const found = coinData.find(
        (i) => i?.base_currency?.toUpperCase() === sym && i?.quote_currency?.toUpperCase() === "USDT"
      );
      if (found) {
        out.push({
          ...found,
          chart_data: hotPairsChart[sym] || found.chart_data,
        });
      }
    }
    if (out.length < 3) {
      const rest = coinData.filter((i) => !out.some((o) => o?._id === i?._id));
      for (let i = 0; i < rest.length && out.length < 3; i += 1) {
        const b = rest[i]?.base_currency;
        out.push({
          ...rest[i],
          chart_data: hotPairsChart?.[b] || rest[i]?.chart_data,
        });
      }
    }
    return out;
  }, [coinData, hotPairsChart]);

  const favoriteRows = useMemo(() => {
    if (!token) return [];
    return filterPairData.filter((item) => item?._id && favCoins.includes(item._id));
  }, [filterPairData, favCoins, token]);

  const favoriteOptionsRows = useMemo(() => {
    if (!token || !favOptions.length) return [];
    return Object.values(optionsContractsByUnderlying)
      .flatMap((list) => (Array.isArray(list) ? list : []))
      .map((c) => mapOptionsContractToMarketRow(c, marketOverview))
      .filter((row) => row?.symbol && favOptions.includes(row.symbol));
  }, [optionsContractsByUnderlying, marketOverview, favOptions, token]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const nextPage = useCallback(
    (data) => {
      if (!data?.base_currency || !data?.quote_currency) return;
      try {
        localStorage.setItem("RecentPair", JSON.stringify(data));
        navigate(`/trade/${data.base_currency}_${data.quote_currency}`);
      } catch {
        // ignore
      }
    },
    [navigate]
  );
  const nextPage2 = useCallback(
    (data) => {
      if (!data?.base_currency || !data?.quote_currency) return;
      try {
        // localStorage.setItem("RecentPair", JSON.stringify(data));
        navigate(`/asset_managemnet/deposit`);
      } catch {
        // ignore
      }
    },
    [navigate]
  );

  const nextFuturesPage = useCallback(
    (data) => {
      const base = data?.short_name || data?.base_asset || data?.base_currency;
      const quote = getFuturesMarginSymbol(data);
      if (!base) return;
      try {
        localStorage.setItem("FuturesPair", JSON.stringify(data));
        navigate(`/futures_trade/${base}_${quote}`);
      } catch {
        // ignore
      }
    },
    [navigate]
  );

  const nextOptionsPage = useCallback(
    (data) => {
      const base = data?.asset || optionAssetFromSymbol(data?.symbol);
      if (!base) return;
      navigate(`/options/${base}_USDT`);
    },
    [navigate]
  );

  const handleAddFav = useCallback(
    async (e, pairId) => {
      e.stopPropagation();
      if (!pairId) return;
      LoaderHelper.loaderStatus(true);
      try {
        const result = await AuthService.favoriteCoin(pairId);
        if (result?.success) {
          const r = await AuthService.favoriteList();
          if (r?.success) setFavCoins(r?.data?.pairs || []);
        } else {
          alertErrorMessage(result?.message || "Failed to update favorite");
        }
      } catch {
        alertErrorMessage("Failed to update favorite");
      }
      LoaderHelper.loaderStatus(false);
    },
    []
  );

  const handleAddOptionsFav = useCallback(
    (e, symbol) => {
      e.stopPropagation();
      e.preventDefault();
      if (!token) {
        navigate("/login");
        return;
      }
      const key = String(symbol || "").trim();
      if (!key) return;
      setFavOptions((prev) => {
        const next = prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key];
        writeOptionsFavSymbols(next);
        return next;
      });
    },
    [token, navigate]
  );

  const handleDetails = useCallback(
    (e, item) => {
      e.stopPropagation();
      e.preventDefault();
      nextPage(item);
    },
    [nextPage]
  );
  const handleDeposit = useCallback(
    (e, item) => {
      e.stopPropagation();
      e.preventDefault();
      nextPage2(item);
    },
    [nextPage2]
  );

  const handleFuturesClick = useCallback(
    (e, item) => {
      e.stopPropagation();
      e.preventDefault();
      nextFuturesPage(item);
    },
    [nextFuturesPage]
  );

  const handleOptionsClick = useCallback(
    (e, item) => {
      e.stopPropagation();
      e.preventDefault();
      nextOptionsPage(item);
    },
    [nextOptionsPage]
  );

  const cycleChangeSort = useCallback((e) => {
    e.stopPropagation();
    setChangeSort((prev) => {
      if (prev === "default") return "desc";
      if (prev === "desc") return "asc";
      return "default";
    });
    setPriceSort("default");
  }, []);

  const cyclePriceSort = useCallback((e) => {
    e.stopPropagation();
    setPriceSort((prev) => {
      if (prev === "default") return "desc";
      if (prev === "desc") return "asc";
      return "default";
    });
    setChangeSort("default");
  }, []);

  const sortArrows = (mode) => {
    if (mode === "default") {
      return (
        <span className="market-th-sort__arrows market-th-sort__arrows--dual" aria-hidden="true">
          <img src="/images/updownarro.svg" alt="" className="market-th-sort__arrowsImg" width={12} height={12} />
        </span>
      );
    }
    return (
      <span className="market-th-sort__arrows" aria-hidden="true">
        <span className={mode === "asc" ? "is-active" : "is-mute"}>↑</span>
        <span className={mode === "desc" ? "is-active" : "is-mute"}>↓</span>
      </span>
    );
  };

  const tableHead = useMemo(
    () => (
      <tr>
        <th>Pair</th>
        <th
          className="market-th-sort"
          onClick={cyclePriceSort}
          title="Sort by last price: high → low → default"
        >
          <span className="market-th-sort__cell">
            <span className="market-th-sort__text">Last Price</span>
            {sortArrows(priceSort)}
          </span>
        </th>
        <th
          className="market-th-sort"
          onClick={cycleChangeSort}
          title="Sort by 24h change: high → low → default"
        >
          <span className="market-th-sort__cell">
            <span className="market-th-sort__text">24H Change</span>
            {sortArrows(changeSort)}
          </span>
        </th>
        <th>
          <span className="dottedline">24h Chart</span>
        </th>
        <th>
          <span className="dottedline">24h Price Range</span>
        </th>
        <th>
          <span className="market_th_dotted_wrap">
            <span className="dottedline">24h Volume</span>
            <span className="updownarro">
              <img src="/images/updownarro.svg" alt="" />
            </span>
          </span>
        </th>
        <th>Action</th>
      </tr>
    ),
    [changeSort, priceSort, cycleChangeSort, cyclePriceSort]
  );

  const tableHeadOptions = useMemo(
    () => (
      <tr>
        <th>
          <span className="market_th_dotted_wrap">
            <span>Name</span>
            <span className="updownarro">
              <img src="/images/updownarro.svg" alt="" />
            </span>
          </span>
        </th>
        <th
          className="market-th-sort"
          onClick={cyclePriceSort}
          title="Sort by price: high → low → default"
        >
          <span className="market-th-sort__cell">
            <span className="market-th-sort__text">Price</span>
            {sortArrows(priceSort)}
          </span>
        </th>
        <th>
          <span className="market_th_dotted_wrap">
            <span>Bid</span>
            <span className="updownarro">
              <img src="/images/updownarro.svg" alt="" />
            </span>
          </span>
        </th>
        <th>
          <span className="market_th_dotted_wrap">
            <span>Ask</span>
            <span className="updownarro">
              <img src="/images/updownarro.svg" alt="" />
            </span>
          </span>
        </th>
        <th>
          <span className="market_th_dotted_wrap">
            <span>Volume</span>
            <span className="updownarro">
              <img src="/images/updownarro.svg" alt="" />
            </span>
          </span>
        </th>
        <th>
          <span className="market_th_dotted_wrap">
            <span>Open Interest</span>
            <span className="updownarro">
              <img src="/images/updownarro.svg" alt="" />
            </span>
          </span>
        </th>
        <th>Actions</th>
      </tr>
    ),
    [priceSort, cyclePriceSort]
  );

  const tableHeadCryptos = useMemo(
    () => (
      <tr>
        <th>Crypto</th>
        <th
          className="market-th-sort"
          onClick={cyclePriceSort}
          title="Sort by last price: high → low → default"
        >
          <span className="market-th-sort__cell">
            <span className="market-th-sort__text">Last Price</span>
            {sortArrows(priceSort)}
          </span>
        </th>
        <th
          className="market-th-sort"
          onClick={cycleChangeSort}
          title="Sort by 24h change: high → low → default"
        >
          <span className="market-th-sort__cell">
            <span className="market-th-sort__text">24H Change</span>
            {sortArrows(changeSort)}
          </span>
        </th>
        <th>
          <span className="dottedline">24h Price Range</span>
        </th>
        <th>Action</th>
      </tr>
    ),
    [changeSort, priceSort, cycleChangeSort, cyclePriceSort]
  );

  const renderDesktopFeatured = (coin) => {
    if (!coin) return null;
    const isPos = isPositiveChange(coin);
    const ch = displaySpotChangePercent(coin);
    const vcls = isPos ? "value text-green" : "value text-danger";
    const last = formatPrice(coin?.buy_price, coin?.tick_size);
    const sym = coin?.base_currency || "—";
    const img = coin?.icon_path ? `${resolvePublicUploadUrl(coin.icon_path)}` : "/images/AnnouncementImg/activities_icon.png";
    return (
      <div className="col-sm-4" key={coin?._id || sym}>
        <Link
          to={`/trade/${coin.base_currency}_${coin.quote_currency}`}
          className="text-decoration-none text-reset d-block"
          onClick={() => {
            try {
              localStorage.setItem("RecentPair", JSON.stringify(coin));
            } catch {
              // ignore
            }
          }}
        >
          <div className="trade_marketvalue" style={{ cursor: "pointer" }}>
            <div className="market_value_card">
              <div className="d-flex tophd">
                <h5>
                  <img
                    alt={sym}
                    src={img}
                    className="img-fluid icon_img coinimg me-2"
                    onError={(e) => {
                      e.target.src = "/images/AnnouncementImg/activities_icon.png";
                    }}
                  />
                  {sym}
                </h5>
                <div className={vcls}>{ch}</div>
              </div>
              <div className="price">${last}</div>
            </div>
            <div className="privevolume">
              <span>24H Volume：</span>
              {formatNumber(coin?.volume)} (USD)
            </div>
            <div className="tradevector_r">
              <MarketMiniSparkline
                isPositive={isPos}
                chartData={buildSpotChartSeries(coin, hotPairsChart)}
              />
            </div>
          </div>
        </Link>
      </div>
    );
  };

  const renderMobileFeatured = (coin) => {
    if (!coin) return null;
    const isPos = isPositiveChange(coin);
    const ch = displaySpotChangePercent(coin);
    const vcls = isPos ? "value text-green" : "value text-danger";
    const last = formatPrice(coin?.buy_price, coin?.tick_size);
    const sym = coin?.base_currency || "—";
    const img = coin?.icon_path ? `${resolvePublicUploadUrl(coin.icon_path)}` : "/images/bitcoin_icon.png";
    return (
      <div className="d-md-none" key={coin?._id || sym}>
        <Link
          to={`/trade/${coin.base_currency}_${coin.quote_currency}`}
          className="text-decoration-none text-reset d-block mb-3"
          onClick={() => {
            try {
              localStorage.setItem("RecentPair", JSON.stringify(coin));
            } catch {
              // ignore
            }
          }}
        >
          <div className="trade_marketvalue" style={{ cursor: "pointer" }}>
            <div className="d-flex tophd">
              <h5>
                <img
                  alt={sym}
                  src={img}
                  className="img-fluid icon_img coinimg me-2"
                  onError={(e) => {
                    e.target.src = "/images/AnnouncementImg/activities_icon.png";
                  }}
                />
                {sym}
              </h5>
              <div className={vcls}>{ch}</div>
            </div>
            <div className="price">${last}</div>
            <div className="privevolume">
              <span>24H Volume：</span>
              {formatNumber(coin?.volume)} (USD)
            </div>
            <div className="tradevector_r">
              <MarketMiniSparkline
                isPositive={isPos}
                chartData={buildSpotChartSeries(coin, hotPairsChart)}
              />
            </div>
          </div>
        </Link>
      </div>
    );
  };

  const spotSubCategoryTabs = (
    <MarketTblTabsScroll recalcKey={`${spotSubCategories.join("|")}|${spotSubCategory}`}>
      <ul className="tbltabs market-tbltabs-list" role="tablist" aria-label="Pair subcategories">
        <li className={spotSubCategory === "All" ? "active" : ""}>
          <button type="button" onClick={() => setSpotSubCategory("All")}>
            All
          </button>
        </li>
        {spotSubCategories.map((k) => (
          <li key={k} className={spotSubCategory === k ? "active" : ""}>
            <button type="button" onClick={() => setSpotSubCategory(k)}>
              {formatSubCategoryLabel(k)}
            </button>
          </li>
        ))}
      </ul>
    </MarketTblTabsScroll>
  );

  return (
    <>
      <Helmet>
        <title>AGCX Market – Live Crypto Prices &amp; Trading Pairs</title>
        <meta
          name="description"
          content="Explore live market data on AGCX. View real-time prices, volumes and trading pairs for Bitcoin, Ethereum and top altcoins. Start trading today."
        />
        <meta
          name="keywords"
          content="crypto market, live crypto prices, bitcoin ethereum trading pairs, AGCX market"
        />
      </Helmet>

      <section className="section-padding login_bg login_sec market_page">
        <div className="market_trade_crypto">
          <div className="container">
            <div className="row d-none d-md-flex">
              {featuredCoins.length > 0
                ? featuredCoins.map((c) => renderDesktopFeatured(c))
                : FEATURED_CARD_SKELETON_SLOTS.map((i) => (
                  <div className="col-sm-4" key={`featured-sk-d-${i}`}>
                    <MarketFeaturedCardSkeleton layout="desktop" index={i} />
                  </div>
                ))}
            </div>
            <div className="d-md-none market_trade_crypto_slider">
              {featuredCoins.length > 0
                ? featuredCoins.map((c) => renderMobileFeatured(c))
                : FEATURED_CARD_SKELETON_SLOTS.map((i) => (
                  <div key={`featured-sk-m-${i}`} className="d-md-none mb-3">
                    <MarketFeaturedCardSkeleton layout="mobile" index={i} />
                  </div>
                ))}
            </div>
          </div>
        </div>

        <section className="live_prices mt-0 market_prices market_update_sec market_update_table">
          <div className="container" ref={gainerElementRef}>
            <div className="d-flex-between mb-3 custom_dlflex">
              <MarketTblTabsScroll className="market-main-pills-scroll" recalcKey="market-main-tabs">
                <ul className="nav nav-pills mb-2 overflowx_scroll funds_tab market_tabs" id="marketMainTabs" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="market-fav-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#market-pane-fav"
                      type="button"
                      role="tab"
                      aria-controls="market-pane-fav"
                      aria-selected="false"
                    >
                      Favorites
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="market-spot-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#market-pane-spot"
                      type="button"
                      role="tab"
                      aria-controls="market-pane-spot"
                      aria-selected="false"
                    >
                      Spot
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link active"
                      id="market-cryptos-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#market-pane-cryptos"
                      type="button"
                      role="tab"
                      aria-controls="market-pane-cryptos"
                      aria-selected="true"
                    >
                      Cryptos
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="market-futures-usdm-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#market-pane-futures-usdm"
                      type="button"
                      role="tab"
                      aria-controls="market-pane-futures-usdm"
                      aria-selected="false"
                    >
                      USDⓈ-M Futures
                    </button>
                  </li>
                  {/* <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="market-futures-coinm-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#market-pane-futures-coinm"
                    type="button"
                    role="tab"
                    aria-controls="market-pane-futures-coinm"
                    aria-selected="false"
                  >
                    COIN-M Futures
                  </button>
                </li> */}
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="market-futures-options-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#market-pane-futures-options"
                      type="button"
                      role="tab"
                      aria-controls="market-pane-futures-options"
                      aria-selected="false"
                    >
                      Options
                    </button>
                  </li>

                  {/* <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="market-alpha-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#market-pane-alpha"
                    type="button"
                    role="tab"
                    aria-controls="market-pane-alpha"
                    aria-selected="false"
                  >
                    Alpha
                  </button>
                </li> */}
                </ul>
              </MarketTblTabsScroll>

              <div className="searchBar custom-tabs">
                <i className="ri-search-2-line"></i>
                <input
                  type="search"
                  className="custom_search"
                  placeholder="Search Crypto"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="tab-content custom-tab-content p-0" id="marketMainTabContent">
              <div className="tab-pane fade" id="market-pane-fav" role="tabpanel" aria-labelledby="market-fav-tab" tabIndex="0">
                <div className="d-flex align-items-center gap-2 w-100 market-tbltabs-row">
                  <MarketQuoteSelect value={pairQuote} onValueChange={setPairQuote} options={spotQuoteOptions} />
                  {spotSubCategoryTabs}
                </div>
                <div className="card py-2">
                  <div className="card-body p-0 desktoptable">
                    {!token ? (
                      <MarketTableEmptyPanel>
                        <Link to="/login" className="market-fav-empty__login">
                          <i className="ri-star-line" aria-hidden="true" />
                          <span>Log in to add favorites</span>
                        </Link>
                      </MarketTableEmptyPanel>
                    ) : spotDataUnavailable ? (
                      <MarketTableEmptyPanel message="No spot market data available." />
                    ) : favoriteRows.length === 0 && favoriteOptionsRows.length === 0 ? (
                      <MarketTableEmptyPanel message="No favorite pairs yet. Add stars from the Spot or Options tab." />
                    ) : (
                      <>
                        {favoriteRows.length > 0 ? (
                          <div className="table-responsive">
                            <table className="table market-spot-pairs-table">
                              <thead>{tableHead}</thead>
                              <tbody>
                                {favoriteRows.map((item, index) => (
                                  <PairTableRow
                                    key={item?._id || index}
                                    item={item}
                                    index={index}
                                    token={token}
                                    favCoins={favCoins}
                                    onFav={handleAddFav}
                                    onTrade={nextPage}
                                    onDetails={handleDetails}
                                    onDeposit={handleDeposit}
                                  />
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : null}
                        {favoriteOptionsRows.length > 0 ? (
                          <div className="table-responsive">
                            <table className="table market-spot-pairs-table">
                              <thead>{tableHeadOptions}</thead>
                              <tbody>
                                {favoriteOptionsRows.map((item, index) => (
                                  <OptionsTableRow
                                    key={item?.symbol || index}
                                    item={item}
                                    index={index}
                                    favSymbols={favOptions}
                                    onFav={handleAddOptionsFav}
                                    onTrade={nextOptionsPage}
                                    onTradeClick={handleOptionsClick}
                                  />
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div
                className="tab-pane fade"
                id="market-pane-spot"
                role="tabpanel"
                aria-labelledby="market-spot-tab"
                tabIndex="0"
              >
                <div className="d-flex align-items-center gap-2 w-100 market-tbltabs-row">
                  <MarketQuoteSelect value={pairQuote} onValueChange={setPairQuote} options={spotQuoteOptions} />
                  {spotSubCategoryTabs}
                </div>

                <div className="card py-2 spot_table">
                  <div className="card-body p-0 desktoptable">
                    <div className="mrt_row"></div>
                    {spotDataUnavailable || filterPairData.length === 0 ? (
                      <MarketTableEmptyPanel
                        message={
                          spotDataUnavailable
                            ? "No spot market data available."
                            : "No pairs match your search or filter."
                        }
                      />
                    ) : (
                      <div className="table-responsive">
                        <table className="table market-spot-pairs-table">
                          <thead>{tableHead}</thead>
                          <tbody>
                            {filterPairData.map((item, index) => (
                              <PairTableRow
                                key={item?._id || index}
                                item={item}
                                index={index}
                                token={token}
                                favCoins={favCoins}
                                onFav={handleAddFav}
                                onTrade={nextPage}
                                onDetails={handleDetails}
                                onDeposit={handleDeposit}
                                showDollarPrice={false}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div
                className="tab-pane fade"
                id="market-pane-futures-usdm"
                role="tabpanel"
                aria-labelledby="market-futures-usdm-tab"
                tabIndex="0"
              >
                <div className="d-flex align-items-center gap-2 w-100 market-tbltabs-row">
                  <MarketQuoteSelect
                    value={futuresQuote}
                    onValueChange={setFuturesQuote}
                    options={futuresQuoteOptions}
                  />
                </div>

                <div className="card py-2">
                  <div className="card-body p-0 desktoptable">
                    <div className="mrt_row"></div>
                    {futuresDataUnavailable || filterFuturesData.length === 0 ? (
                      <MarketTableEmptyPanel
                        message={
                          futuresDataUnavailable
                            ? "No futures market data available."
                            : "No contracts match your search or filter."
                        }
                      />
                    ) : (
                      <div className="table-responsive">
                        <table className="table market-spot-pairs-table">
                          <thead>{tableHead}</thead>
                          <tbody>
                            {filterFuturesData.map((item, index) => (
                              <FuturesTableRow
                                key={item?._id || index}
                                item={item}
                                index={index}
                                onFutures={nextFuturesPage}
                                onFuturesClick={handleFuturesClick}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* <div
                className="tab-pane fade"
                id="market-pane-futures-coinm"
                role="tabpanel"
                aria-labelledby="market-futures-coinm-tab"
                tabIndex="0"
              >
                <div className="card py-2">
                  <div className="card-body p-0 desktoptable market-futures-placeholder">
                    <div className="market-fav-empty" role="status">
                      <img
                        src="/images/no-data.svg"
                        alt=""
                        width={120}
                        height={144}
                        className="market-fav-empty__img"
                      />
                      <p className="market-fav-empty__caption">COIN-M futures markets are not available yet.</p>
                    </div>
                  </div>
                </div>
              </div> */}

              <div
                className="tab-pane fade"
                id="market-pane-futures-options"
                role="tabpanel"
                aria-labelledby="market-futures-options-tab"
                tabIndex="0"
              >
                {optionsQuoteOptions.length > 0 ? (
                  <div className="d-flex align-items-center gap-2 w-100 market-tbltabs-row">
                    <MarketQuoteSelect
                      value={optionsAsset}
                      onValueChange={setOptionsAsset}
                      options={optionsQuoteOptions}
                    />
                  </div>
                ) : null}
                <div className="card py-2">
                  <div className="card-body p-0 desktoptable">
                    <div className="mrt_row"></div>
                    {optionsLoading || optionsDataUnavailable || filterOptionsData.length === 0 ? (
                      <MarketTableEmptyPanel
                        message={
                          optionsLoading
                            ? "Loading options markets..."
                            : optionsDataUnavailable
                              ? "No options market data available."
                              : "No contracts match your search or filter."
                        }
                      />
                    ) : (
                      <div className="table-responsive">
                        <table className="table market-spot-pairs-table">
                          <thead>{tableHeadOptions}</thead>
                          <tbody>
                            {filterOptionsData.map((item, index) => (
                              <OptionsTableRow
                                key={item?.symbol || index}
                                item={item}
                                index={index}
                                favSymbols={favOptions}
                                onFav={handleAddOptionsFav}
                                onTrade={nextOptionsPage}
                                onTradeClick={handleOptionsClick}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="tab-pane fade show active" id="market-pane-cryptos" role="tabpanel" aria-labelledby="market-cryptos-tab" tabIndex="0">
                <div className="d-flex align-items-center gap-2 w-100 market-tbltabs-row">
                  <MarketQuoteSelect value={pairQuote} onValueChange={setPairQuote} options={spotQuoteOptions} />
                  {spotSubCategoryTabs}
                </div>

                <div className="card py-2 spot_table">
                  <div className="card-body p-0 desktoptable">
                    <div className="mrt_row"></div>
                    {spotDataUnavailable || filterCryptosData.length === 0 ? (
                      <MarketTableEmptyPanel
                        message={
                          spotDataUnavailable
                            ? "No spot market data available."
                            : "No cryptos match your search or filter."
                        }
                      />
                    ) : (
                      <div className="table-responsive">
                        <table className="table market-spot-pairs-table market-cryptos-pairs-table">
                          <thead>{tableHeadCryptos}</thead>
                          <tbody>
                            {filterCryptosData.map((item, index) => (
                              <PairTableRow
                                key={`${item?.base_currency || "x"}-${item?._id || index}`}
                                item={item}
                                index={index}
                                token={token}
                                favCoins={favCoins}
                                onFav={handleAddFav}
                                onTrade={nextPage}
                                onDetails={handleDetails}
                                onDeposit={handleDeposit}
                                variant="cryptos"
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* <div className="tab-pane fade" id="market-pane-alpha" role="tabpanel" aria-labelledby="market-alpha-tab" tabIndex="0">
                <div className="d-flex align-items-center gap-2 w-100 market-alpha-row">
                  <MarketTblTabsScroll>
                    <ul className="tbltabs market-tbltabs-list">
                      <li className="active">
                        <button type="button">All Chain</button>
                      </li>
                      <li>
                        <button type="button">Pump</button>
                      </li>
                      <li>
                        <button type="button">Top Searches</button>
                      </li>
                      <li>
                        <button type="button">New</button>
                      </li>
                      <li>
                        <button type="button">Themes</button>
                      </li>
                    </ul>
                  </MarketTblTabsScroll>

                  <div className="market-alpha-filters">
                    <AlphaChainSelect />

                    <AlphaPoolSelect />
                  </div>
                </div>

                <div className="card py-2 spot_table">
                  <div className="card-body p-0 desktoptable">
                    <div className="mrt_row"></div>
                    {spotDataUnavailable || filterPairData.length === 0 ? (
                      <MarketTableEmptyPanel
                        message={
                          spotDataUnavailable
                            ? "No spot market data available."
                            : "No pairs match your search or filter."
                        }
                      />
                    ) : (
                      <div className="table-responsive">
                        <table className="table market-spot-pairs-table">
                          <thead>{tableHead}</thead>
                          <tbody>
                            {filterPairData.map((item, index) => (
                              <PairTableRow
                                key={item?._id || index}
                                item={item}
                                index={index}
                                token={token}
                                favCoins={favCoins}
                                onFav={handleAddFav}
                                onTrade={nextPage}
                                onDetails={handleDetails}
                                onDeposit={handleDeposit}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </section>
      </section>
      <Footer />
    </>
  );
};

export default Market;
